import ODAS from '@/utils/api/odas';

/**
 * Resolves the fetchable type the API expects (snake_case `fetchable_type`).
 *
 * Accepts either the type string itself or a resource object. React components
 * are split on which spelling they carry - API payloads use `resource_type`,
 * while some UI-facing objects are built with the camelCase `resourceType` - so
 * this API layer is the single place that reconciles them.
 *
 * @param {string|Object} typeOrResource - a type string, or a resource carrying one
 * @returns {string|undefined} the resource type, or undefined when none is present
 */
export const fetchableTypeOf = (typeOrResource) => {
  if (typeof typeOrResource === 'string') {
    return typeOrResource;
  }
  return typeOrResource?.resource_type || typeOrResource?.resourceType;
};

const Collection = {
  loadMyCollections: (params) => {
    return ODAS.get(`/api/account/collections/all`, params);
  },
  loadMyEditableCollections: (params) => {
    return ODAS.get(`/api/account/collections/editable`, params);
  },
  loadOrgCollections: (params) => {
    return ODAS.get(`/api/collections`, params);
  },
  loadTeamCollections: (params) => {
    return ODAS.get(`/api/collections`, params);
  },
  searchCollections: (params) => {
    return ODAS.get(`/api/collections`, params);
  },
  createCollection: (params) => {
    return ODAS.post(`/api/collections/`, params);
  },
  updateCollection: (collectionId, params) => {
    return ODAS.put(`/api/collections/${collectionId}`, params);
  },
  deleteCollection: (collectionId) => {
    return ODAS.delete(`/api/collections/${collectionId}`);
  },
  addToCollection: (fetchableId, fetchableType, collectionId, categoryId) => {
    const type = fetchableTypeOf(fetchableType);
    if (!type) {
      return Promise.reject(new Error('addToCollection called without fetchable_type'));
    }
    let endpoint = '/api/collections/' + collectionId;
    if (categoryId) {
      endpoint = endpoint + '/categories/' + categoryId;
    }
    endpoint = endpoint + '/items';

    return ODAS.post(endpoint, { fetchable_id: fetchableId, fetchable_type: type });
  },

  removeFromCollection: (fetchableId, fetchableType, collectionId) => {
    const type = fetchableTypeOf(fetchableType);
    if (!type) {
      return Promise.reject(new Error('removeFromCollection called without fetchable_type'));
    }
    let endpoint = '/api/collections/' + collectionId + '/items/' + fetchableId;
    return ODAS.delete(endpoint, { fetchable_type: type });
  },
  removeFromCollectionCategory: (fetchableId, fetchableType, collectionId, categoryId) => {
    const type = fetchableTypeOf(fetchableType);
    if (!type) {
      return Promise.reject(new Error('removeFromCollectionCategory called without fetchable_type'));
    }
    let endpoint = '/api/collections/' + collectionId + '/categories/' + categoryId + '/items/' + fetchableId;
    return ODAS.delete(endpoint, { fetchable_type: type });
  },
  loadCollectionBySlug: (slug, params) => {
    return ODAS.get('/api/collections/' + slug, params);
  },
  addNewCategory: (collection, params) => {
    return ODAS.post(`api/collections/${collection.id}/categories`, params);
  },
  updatePermission: (collectionId, permission, newPermissionType) => {
    return ODAS.put('/api/collections/' + collectionId + '/permissions/' + permission.permissionId, {
      new_permission_type: newPermissionType
    });
  },
  inviteCollaborators: (collectionId, emails, permissionType, noAlert = false) => {
    return ODAS.post('/api/collections/' + collectionId + '/permissions', {
      permission_type: permissionType,
      emails: emails,
      no_alert: noAlert
    });
  },
  deletePermission: (collectionId, permissionId) => {
    return ODAS.delete('/api/collections/' + collectionId + '/permissions/' + permissionId);
  },
  getCategory: function (collectionId, categoryId) {
    return ODAS.get('/api/collections/' + collectionId + '/categories/' + categoryId);
  },
  deleteCategory: (collectionId, categoryId) => {
    return ODAS.delete('/api/collections/' + collectionId + '/categories/' + categoryId);
  },
  updateCategory: (collectionId, category) => {
    return ODAS.put('/api/collections/' + collectionId + '/categories/' + category.id, { category: category });
  }
};

export default Collection;
